import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Candidate } from './candidate';
export { Candidate };
import { candidateSchema } from '@schemas/business-objects/candidate';
import { AddressFormGroup } from './address-form-group';
import { ClassEnrollmentFormArray } from './class-enrollment-form-group';
import { ContactDetailsFormGroup } from './contact-details-form-group';
import { EmergencyContactFormGroup } from './emergency-contact-form-group';
import { EventAvailabilityFormGroup } from './event-availability-form-group';
import { IdentificationDocumentFormGroup } from './identification-document-form-group';
import { InterviewFormArray } from './interview-form-group';
import { LanguageAndSkillsFormGroup } from './language-and-skills-form-group';
import { PersonalInformationFormGroup } from './personal-information-form-group';
import { PlacementFormArray } from './placement-form-group';
import { SkillsAndExperienceFormGroup } from './skills-and-experience-form-group';
import { StaffTypeFormGroup } from './staff-type-form-group';
import { UserFormGroup } from './user-form-group';

export class CandidateFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    status: SchemaFormControl;
    staffTypeId: SchemaFormControl;
    staffType: StaffTypeFormGroup;
    placements: PlacementFormArray;
    classEnrollments: ClassEnrollmentFormArray;
    address: AddressFormGroup;
    personalInformation: PersonalInformationFormGroup;
    identificationDocument: IdentificationDocumentFormGroup;
    contactDetails: ContactDetailsFormGroup;
    emergencyContact: EmergencyContactFormGroup;
    skillsAndExperience: SkillsAndExperienceFormGroup;
    languageAndSkills: LanguageAndSkillsFormGroup;
    eventAvailability: EventAvailabilityFormGroup;
    interviews: InterviewFormArray;
    userId: SchemaFormControl;
    user: UserFormGroup;
  };

  declare value: Partial<Candidate>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(candidateSchema, Candidate, data, options);
  }

  get object(): Candidate {
    return new Candidate(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'staffType':
        return new StaffTypeFormGroup();
      case 'placements':
        return new PlacementFormArray();
      case 'classEnrollments':
        return new ClassEnrollmentFormArray();
      case 'address':
        return new AddressFormGroup();
      case 'personalInformation':
        return new PersonalInformationFormGroup();
      case 'identificationDocument':
        return new IdentificationDocumentFormGroup();
      case 'contactDetails':
        return new ContactDetailsFormGroup();
      case 'emergencyContact':
        return new EmergencyContactFormGroup();
      case 'skillsAndExperience':
        return new SkillsAndExperienceFormGroup();
      case 'languageAndSkills':
        return new LanguageAndSkillsFormGroup();
      case 'eventAvailability':
        return new EventAvailabilityFormGroup();
      case 'interviews':
        return new InterviewFormArray();
      case 'user':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }
}

export class CandidateFormArray extends PersistedFormArray {
  readonly entityName = 'Candidate' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(candidateSchema, Candidate, options);
  }

  protected override createItemGroup() {
    return new CandidateFormGroup();
  }

  get array(): Candidate[] {
    return this.controls.map((group: CandidateFormGroup) => group.object);
  }
}

export class CandidatePersistedArray extends PersistedArray<Candidate> {
  readonly entityName = 'Candidate' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Candidate, options);
  }
}
